import { useCallback, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { Spotlight } from './useNthUserOrientationStore';

interface UseSpotlightAnalyticsProps {
	spotlightId: Spotlight;
	isVisible: boolean;
	onNext?: () => void;
	onBack?: () => void;
	onDismiss: () => void;
}

const SPOTLIGHT_ACTION_SUBJECT_IDS = {
	'nth-user-orientation-home-for-you-spotlight': 'nthUserOrientationHomeForYouSpotlight',
	'nth-user-orientation-home-space-spotlight': 'nthUserOrientationHomeSpaceSpotlight',
	'nth-user-orientation-home-template-spotlight': 'nthUserOrientationHomeTemplateSpotlight',
	'nth-user-orientation-home-create-spotlight': 'nthUserOrientationHomeCreateSpotlight',
	'nth-user-orientation-editor-space-spotlight': 'nthUserOrientationEditorSpaceSpotlight',
	'nth-user-orientation-editor-for-you-spotlight': 'nthUserOrientationEditorForYouSpotlight',
	'nth-user-orientation-editor-bpqa-spotlight': 'nthUserOrientationEditorBpqaSpotlight',
} as const satisfies Record<Spotlight, string>;

export function useSpotlightAnalytics<T extends UseSpotlightAnalyticsProps>({
	spotlightId,
	isVisible,
	onNext,
	onBack,
	onDismiss,
}: T): {
	handleNext: T['onNext'];
	handleBack: T['onBack'];
	handleDismiss: () => void;
} {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Fire render event when spotlight becomes visible
	useEffect(() => {
		if (isVisible) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'rendered',
					actionSubject: 'spotlight',
					actionSubjectId: SPOTLIGHT_ACTION_SUBJECT_IDS[spotlightId],
					source: 'nth-user-orientation',
				},
			}).fire();
		}
	}, [isVisible, createAnalyticsEvent, spotlightId]);

	const handleNext = useCallback(() => {
		if (onNext) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'next',
					source: spotlightId,
				},
			}).fire();
			onNext();
		}
	}, [onNext, createAnalyticsEvent, spotlightId]);

	const handleBack = useCallback(() => {
		if (onBack) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'back',
					source: spotlightId,
				},
			}).fire();
			onBack();
		}
	}, [onBack, createAnalyticsEvent, spotlightId]);

	const handleDismiss = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissTour',
				source: spotlightId,
			},
		}).fire();
		onDismiss();
	}, [onDismiss, createAnalyticsEvent, spotlightId]);

	return {
		handleNext: (onNext ? handleNext : undefined) as T['onNext'],
		handleBack: (onBack ? handleBack : undefined) as T['onBack'],
		handleDismiss,
	};
}
